import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { KeyRound, RefreshCw, Search, Eye, Plus, Zap, ShieldCheck, ShieldOff, Ban, Clock } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { LicenseStatusBadge, PlanBadge, formatQuota } from "./licenses/LicenseBadges";

const TRIGGER = "bg-white/5 border-white/10 text-white hover:bg-white/10";
const CONTENT = "z-[110] bg-[#0F0F17] border-white/10 text-white";
const ITEM = "text-white focus:bg-white/10 focus:text-white";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } }

function SummaryCard({ label, value, icon: Icon, tone = "neutral", testid }) {
  const tones = {
    neutral: "text-white", emerald: "text-emerald-300", amber: "text-amber-300",
    rose: "text-rose-300", slate: "text-slate-300", purple: "text-purple-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PlatformLicenses() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(true);

  // create form
  const [newTenantId, setNewTenantId] = useState("");
  const [newPlan, setNewPlan] = useState("STARTER");
  const [newDuration, setNewDuration] = useState("30");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (plan) params.plan_code = plan;
      const r = await api.get("/platform/licenses", { params });
      setItems(r.data.items || []);
      setSummary(r.data.summary || null);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [q, status, plan]);

  useEffect(() => { load(); }, [load]);

  const createLicense = async () => {
    if (!newTenantId.trim()) { toast.error("Tenant ID wajib diisi."); return; }
    setCreating(true);
    try {
      const r = await api.post("/platform/licenses", {
        tenant_id: newTenantId.trim(), plan_code: newPlan, duration_days: parseInt(newDuration, 10),
      });
      toast.success(`License ${r.data.license.license_key} dibuat.`);
      setNewTenantId("");
      load();
    } catch (e) { notifyError(e, "Gagal membuat license"); }
    finally { setCreating(false); }
  };

  const doAction = async (tenantId, action, extra = {}) => {
    try {
      const r = await api.post(`/platform/licenses/${tenantId}/${action}`, extra);
      toast.success(`License ${action} berhasil.`);
      load();
      return r.data.license;
    } catch (e) { notifyError(e, `${action} gagal`); }
  };

  return (
    <PlatformLayout title="License">
      <div data-testid="platform-licenses-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <SummaryCard label="Active" value={summary?.active ?? 0} tone="emerald" icon={ShieldCheck} testid="lic-summary-active" />
          <SummaryCard label="Grace" value={summary?.grace_period ?? 0} tone="amber" icon={Clock} testid="lic-summary-grace" />
          <SummaryCard label="Suspended" value={summary?.suspended ?? 0} tone="rose" icon={ShieldOff} testid="lic-summary-suspended" />
          <SummaryCard label="Expired" value={summary?.expired ?? 0} tone="slate" icon={Clock} testid="lic-summary-expired" />
          <SummaryCard label="Revoked" value={summary?.revoked ?? 0} tone="rose" icon={Ban} testid="lic-summary-revoked" />
          <SummaryCard label="Enterprise" value={summary?.enterprise ?? 0} tone="purple" icon={Zap} testid="lic-summary-enterprise" />
        </div>

        {/* Create License */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Plus size={14} className="text-emerald-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Terbitkan License Baru</div>
          </div>
          <div className="grid md:grid-cols-4 gap-2">
            <Input value={newTenantId} onChange={(e) => setNewTenantId(e.target.value)} placeholder="Tenant ID"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="lic-new-tenant" />
            <Select value={newPlan} onValueChange={setNewPlan}>
              <SelectTrigger className={TRIGGER} data-testid="lic-new-plan"><SelectValue /></SelectTrigger>
              <SelectContent className={CONTENT}>
                {["STARTER", "GROWTH", "ENTERPRISE"].map((p) => <SelectItem key={p} value={p} className={ITEM}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={newDuration} onChange={(e) => setNewDuration(e.target.value)} type="number" min="1" max="3650" placeholder="Duration days"
              className="bg-white/5 border-white/10 text-white" data-testid="lic-new-duration" />
            <Button onClick={createLicense} disabled={creating} className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30" data-testid="lic-new-submit">
              {creating ? "Menerbitkan..." : "Terbitkan License"}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari tenant / license key…"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-9" data-testid="lic-search" />
          </div>
          <Select value={status || "ALL"} onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-44 ${TRIGGER}`} data-testid="lic-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "ACTIVE", "GRACE_PERIOD", "SUSPENDED", "EXPIRED", "REVOKED"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={plan || "ALL"} onValueChange={(v) => setPlan(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-40 ${TRIGGER}`} data-testid="lic-filter-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "STARTER", "GROWTH", "ENTERPRISE"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="lic-reload">
            <RefreshCw size={14} className="mr-1" /> Reload
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="lic-table-loading">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center" data-testid="lic-table-empty">Belum ada license. Terbitkan license pertama Anda di form di atas.</div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="lic-table">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Tenant</div>
              <div className="col-span-2">License Key</div>
              <div className="col-span-1">Plan</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Website</div>
              <div className="col-span-1">Storage</div>
              <div className="col-span-1">Deploy</div>
              <div className="col-span-1">Expire</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {items.map((l) => {
              const q = (l.entitlements || {}).quota || {};
              return (
                <div key={l.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center" data-testid={`lic-row-${l.tenant_id}`}>
                  <div className="col-span-3 min-w-0">
                    <Link to={`/platform/licenses/${l.tenant_id}`} className="text-sm font-bold text-white hover:text-emerald-300 truncate block" data-testid={`lic-tenant-${l.tenant_id}`}>{l.tenant_name}</Link>
                    <div className="text-[10px] text-white/40 font-mono truncate">{l.tenant_id}</div>
                  </div>
                  <div className="col-span-2 text-[10px] font-mono text-white/80 truncate" title={l.license_key}>{l.license_key}</div>
                  <div className="col-span-1"><PlanBadge plan={l.plan_code} /></div>
                  <div className="col-span-1"><LicenseStatusBadge status={l.status} /></div>
                  <div className="col-span-1 text-xs font-mono text-white/80">{formatQuota(q.website)}</div>
                  <div className="col-span-1 text-xs font-mono text-white/80">{formatQuota(q.storage_gb)}{q.storage_gb === -1 ? "" : " GB"}</div>
                  <div className="col-span-1 text-xs font-mono text-white/80">{formatQuota(q.deployment)}</div>
                  <div className="col-span-1 text-[10px] text-white/60">{fmt(l.expires_at)}</div>
                  <div className="col-span-1 flex gap-1 justify-end">
                    {l.status !== "REVOKED" && l.status !== "ACTIVE" && (
                      <button onClick={() => doAction(l.tenant_id, "activate", { note: "" })} className="w-7 h-7 rounded-lg grid place-items-center text-emerald-300 hover:bg-emerald-500/20" title="Activate" data-testid={`lic-activate-${l.tenant_id}`}>
                        <ShieldCheck size={12} />
                      </button>
                    )}
                    {l.status !== "REVOKED" && l.status !== "SUSPENDED" && (
                      <button onClick={() => {
                        const reason = window.prompt("Alasan suspend?");
                        if (reason && reason.length >= 3) doAction(l.tenant_id, "suspend", { reason });
                      }} className="w-7 h-7 rounded-lg grid place-items-center text-amber-300 hover:bg-amber-500/20" title="Suspend" data-testid={`lic-suspend-${l.tenant_id}`}>
                        <ShieldOff size={12} />
                      </button>
                    )}
                    <Link to={`/platform/licenses/${l.tenant_id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10" title="Detail" data-testid={`lic-view-${l.tenant_id}`}>
                      <Eye size={12} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
